import { parseLocaleNumber, roundMoneyAmount } from "./Utils";
import {
  ShopTvaSettings,
  toCustomerDisplayPrice,
} from "./tvaDisplayPrice";

/** Article à taille et prix variable : chaque variation a son propre prix. */
export function isVariablePriceProduct(item: any): boolean {
  return item?.type === "taille_variable";
}

function getAllVariations(item: any): any[] {
  return item?.variations ?? [];
}

/** Normalise un booléen API (bool, "true"/"false", 0/1). */
function normalizeBoolFlag(
  value: unknown,
): boolean | null | undefined {
  if (value === undefined) return undefined;
  if (value === null) return null;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  const s = String(value).trim().toLowerCase();
  if (s === "true" || s === "1") return true;
  if (s === "false" || s === "0" || s === "") return false;
  return undefined;
}

/**
 * Variation en stock illimité (gestion de stock désactivée au niveau variation).
 * Aligné sur easy-market-vendeur-mobile quand stock_management=false.
 */
export function isVariationStockUnlimited(variation: any): boolean {
  if (!variation) return false;
  return normalizeBoolFlag(variation.stock_management) === false;
}

/** Variation commandable (quantité > 0 ou stock illimité). */
export function isVariationInStock(variation: any): boolean {
  if (!variation) return false;
  if (isVariationStockUnlimited(variation)) return true;
  return parseLocaleNumber(variation.quantite) > 0;
}

/**
 * Produit sans suivi de stock → stock illimité
 * (stock_tracking=false / null, ou boutique avec stock_management=false).
 */
export function isProductStockUnlimited(item: any): boolean {
  if (!item) return false;
  const tracking = normalizeBoolFlag(item.stock_tracking);
  if (tracking === false || tracking === null) return true;
  const vendeurTracking = normalizeBoolFlag(item?.vendeur?.stock_management);
  if (vendeurTracking === false) return true;
  return false;
}

function getAvailableVariations(item: any): any[] {
  const variations = getAllVariations(item);
  if (isProductStockUnlimited(item)) return variations;
  const inStock = variations.filter((v: any) => isVariationInStock(v));
  return inStock.length > 0 ? inStock : variations;
}

function getVariationPricesFromList(variations: any[]): number[] {
  return variations
    .map((v: any) => parseLocaleNumber(v?.prix))
    .filter((p: number) => Number.isFinite(p) && p > 0);
}

/** Prix catalogue : toutes les variations (y compris rupture) pour le min / « À partir de ». */
function getCatalogVariationPrices(item: any): number[] {
  return getVariationPricesFromList(getAllVariations(item));
}

/**
 * « À partir de » pour les articles à taille et prix variable :
 * dès qu'il y a plusieurs tailles ou plusieurs prix distincts.
 */
function shouldShowFromPrice(item: any): boolean {
  if (!isVariablePriceProduct(item)) {
    return getCatalogVariationPrices(item).length > 1;
  }

  const allVariations = getAllVariations(item);
  const prices = getVariationPricesFromList(allVariations);
  const sizes = getDistinctSizes(allVariations);
  const uniquePriceCount = Array.from(new Set(prices)).length;

  if (sizes.length > 1 || uniquePriceCount > 1) {
    return true;
  }

  return false;
}

function getDistinctColors(variations: any[]): string[] {
  const colors = new Set<string>();
  for (const v of variations) {
    if (v?.couleur != null && String(v.couleur).trim() !== "") {
      colors.add(String(v.couleur));
    }
  }
  return Array.from(colors);
}

function getDistinctSizes(variations: any[]): string[] {
  const sizes = new Set<string>();
  for (const v of variations) {
    if (v?.taille != null && String(v.taille).trim() !== "") {
      sizes.add(String(v.taille));
    }
  }
  return Array.from(sizes);
}

export function needsSizeSelection(item: any): boolean {
  const type = item?.type;
  return type === "taille_variable" || type === "taille_variable_prix_unique";
}

export function needsColorSelection(item: any): boolean {
  return item?.type === "taille_unique_avec_couleur";
}

/**
 * Libellé du bouton catalogue.
 * Retourne null si une seule option (ou aucune) → ajout direct au panier.
 */
export function getCatalogSelectionLabel(item: any): string | null {
  const type = item?.type;
  const variations = getAllVariations(item);
  const colors = getDistinctColors(variations);
  const sizes = getDistinctSizes(variations);

  if (type === "taille_unique_avec_couleur") {
    return colors.length > 1 ? "Choisir la couleur" : null;
  }

  if (type === "taille_variable" || type === "taille_variable_prix_unique") {
    if (sizes.length > 1) return "Choisir la taille";
    if (colors.length > 1) return "Choisir la couleur";
    return null;
  }

  return null;
}

/** Produit nécessitant un choix de variation avant ajout au panier. */
export function needsVariationSelection(item: any): boolean {
  return getCatalogSelectionLabel(item) !== null;
}

export function getPromoInfo(item: any): { hasPromo: boolean; promoTaux: number } {
  const hasPromo =
    item?.categorie?.promotion?.is_active ||
    item?.sous_categorie?.promotion?.is_active ||
    item?.promotion?.is_active;

  const promoTaux =
    item?.categorie?.promotion?.taux ||
    item?.sous_categorie?.promotion?.taux ||
    item?.promotion?.taux ||
    0;

  return { hasPromo, promoTaux };
}

function applyPromo(base: number, hasPromo: boolean, promoTaux: number): number {
  if (!hasPromo || base <= 0) return base;
  return roundMoneyAmount(base - (base * promoTaux) / 100);
}

function withCustomerTvaPrices<T extends { displayPrice: number; originalPrice: number }>(
  result: T,
  tvaSettings?: ShopTvaSettings | null,
): T {
  if (!tvaSettings) return result;
  return {
    ...result,
    displayPrice: toCustomerDisplayPrice(result.displayPrice, tvaSettings),
    originalPrice: toCustomerDisplayPrice(result.originalPrice, tvaSettings),
  };
}

export function getVariationUnitPrices(item: any): number[] {
  return getCatalogVariationPrices(item);
}

export function getCatalogPriceInfo(
  item: any,
  tvaSettings?: ShopTvaSettings | null,
): {
  displayPrice: number;
  originalPrice: number;
  isFromPrice: boolean;
  hasPromo: boolean;
  promoTaux: number;
} {
  const { hasPromo, promoTaux } = getPromoInfo(item);
  const isFromPrice = shouldShowFromPrice(item);

  if (isVariablePriceProduct(item)) {
    const prices = getCatalogVariationPrices(item);
    if (prices.length > 0) {
      const min = Math.min(...prices);
      return withCustomerTvaPrices(
        {
          displayPrice: applyPromo(min, hasPromo, promoTaux),
          originalPrice: min,
          isFromPrice,
          hasPromo,
          promoTaux,
        },
        tvaSettings,
      );
    }
  }

  let base = parseLocaleNumber(item?.prix);
  if (base <= 0) {
    const prices = getCatalogVariationPrices(item);
    if (prices.length > 0) {
      const min = Math.min(...prices);
      return withCustomerTvaPrices(
        {
          displayPrice: applyPromo(min, hasPromo, promoTaux),
          originalPrice: min,
          isFromPrice: isFromPrice || prices.length > 1,
          hasPromo,
          promoTaux,
        },
        tvaSettings,
      );
    }
  }

  return withCustomerTvaPrices(
    {
      displayPrice: applyPromo(base, hasPromo, promoTaux),
      originalPrice: base,
      isFromPrice: false,
      hasPromo,
      promoTaux,
    },
    tvaSettings,
  );
}

/**
 * Prix unitaire fiche produit : prix de la variation sélectionnée
 * (taille_variable) ou prix produit, avec promo appliquée.
 */
export function getProductDetailPriceInfo(
  item: any,
  variation?: any | null,
  options?: { sizeSelected?: boolean },
  tvaSettings?: ShopTvaSettings | null,
): {
  displayPrice: number;
  originalPrice: number;
  isFromPrice: boolean;
  hasPromo: boolean;
  promoTaux: number;
} {
  const { hasPromo, promoTaux } = getPromoInfo(item);
  const sizeSelected = options?.sizeSelected ?? true;

  if (isVariablePriceProduct(item)) {
    const variationPrice = parseLocaleNumber(variation?.prix);
    if (sizeSelected && variationPrice > 0) {
      return withCustomerTvaPrices(
        {
          displayPrice: applyPromo(variationPrice, hasPromo, promoTaux),
          originalPrice: variationPrice,
          isFromPrice: false,
          hasPromo,
          promoTaux,
        },
        tvaSettings,
      );
    }

    const prices = getCatalogVariationPrices(item);
    if (prices.length > 0) {
      const min = Math.min(...prices);
      return withCustomerTvaPrices(
        {
          displayPrice: applyPromo(min, hasPromo, promoTaux),
          originalPrice: min,
          isFromPrice: true,
          hasPromo,
          promoTaux,
        },
        tvaSettings,
      );
    }
  }

  let base = parseLocaleNumber(item?.prix);
  if (base <= 0) {
    const variationPrice = parseLocaleNumber(variation?.prix);
    if (variationPrice > 0) {
      base = variationPrice;
    } else {
      const prices = getCatalogVariationPrices(item);
      if (prices.length > 0) {
        base = Math.min(...prices);
      }
    }
  }

  return withCustomerTvaPrices(
    {
      displayPrice: applyPromo(base, hasPromo, promoTaux),
      originalPrice: base,
      isFromPrice: false,
      hasPromo,
      promoTaux,
    },
    tvaSettings,
  );
}

export type WholesaleTier = {
  min_quantity: number;
  unit_price: string | number;
  scope?: string;
};

/** Paliers produit ou variation (aligné app vendeur). */
export function getWholesaleTiersForProductOrVariation(
  product: any,
  variation?: any | null,
): WholesaleTier[] {
  if (Array.isArray(variation?.wholesale_tiers) && variation.wholesale_tiers.length > 0) {
    return variation.wholesale_tiers;
  }
  const productTiers = product?.wholesale_tiers;
  if (!Array.isArray(productTiers) || productTiers.length === 0) return [];
  const scoped = productTiers.filter(
    (t: WholesaleTier) => !t.scope || t.scope === "PRODUCT",
  );
  return scoped.length > 0 ? scoped : productTiers;
}

/**
 * Applique le palier en gros le plus élevé dont min_quantity <= qte.
 * Le unit_price du palier remplace le prix détail (sans cumul promo).
 */
export function computeEffectiveUnitPrice(
  basePrice: number,
  qte: number,
  wholesaleTiers?: WholesaleTier[] | null,
): number {
  if (!wholesaleTiers?.length || qte <= 0) return basePrice;
  const sorted = [...wholesaleTiers].sort(
    (a, b) => Number(b.min_quantity) - Number(a.min_quantity),
  );
  const applicable = sorted.find((tier) => qte >= Number(tier.min_quantity));
  if (!applicable) return basePrice;
  const wholesale = parseLocaleNumber(applicable.unit_price);
  return wholesale > 0 ? wholesale : basePrice;
}

export function getLowestWholesaleTier(
  product: any,
  variation?: any | null,
): WholesaleTier | null {
  const tiers = getWholesaleTiersForProductOrVariation(product, variation);
  if (!tiers.length) return null;
  return [...tiers].sort(
    (a, b) => Number(a.min_quantity) - Number(b.min_quantity),
  )[0];
}

/**
 * Prix unitaire fiche / panier selon la quantité (prix de gros catalogue).
 */
export function getEffectiveCatalogUnitPrice(
  item: any,
  variation: any | null | undefined,
  quantity: number,
  options?: { sizeSelected?: boolean },
  tvaSettings?: ShopTvaSettings | null,
): {
  displayPrice: number;
  originalPrice: number;
  retailDisplayPrice: number;
  isWholesale: boolean;
  isFromPrice: boolean;
  hasPromo: boolean;
  promoTaux: number;
} {
  const retail = getProductDetailPriceInfo(
    item,
    variation,
    options,
    tvaSettings,
  );
  const tiers = getWholesaleTiersForProductOrVariation(item, variation);
  if (!tiers.length || quantity <= 0) {
    return {
      ...retail,
      retailDisplayPrice: retail.displayPrice,
      isWholesale: false,
    };
  }

  // Prix hors TVA pour appliquer le palier, puis réappliquer l'affichage TVA client
  const retailBeforeTva = getProductDetailPriceInfo(
    item,
    variation,
    options,
    null,
  );
  const effectiveBeforeTva = computeEffectiveUnitPrice(
    retailBeforeTva.displayPrice,
    quantity,
    tiers,
  );
  const isWholesale =
    effectiveBeforeTva > 0 &&
    effectiveBeforeTva < retailBeforeTva.displayPrice;

  if (!isWholesale) {
    return {
      ...retail,
      retailDisplayPrice: retail.displayPrice,
      isWholesale: false,
    };
  }

  const wholesaleDisplay = tvaSettings
    ? toCustomerDisplayPrice(effectiveBeforeTva, tvaSettings)
    : roundMoneyAmount(effectiveBeforeTva);

  return {
    displayPrice: wholesaleDisplay,
    originalPrice: retail.originalPrice,
    retailDisplayPrice: retail.displayPrice,
    isWholesale: true,
    isFromPrice: false,
    hasPromo: false,
    promoTaux: retail.promoTaux,
  };
}

/**
 * Produit disponible à la vente (pas en rupture).
 * Respecte stock_tracking / stock_management : si désactivés → stock illimité.
 */
export function hasProductStock(item: any): boolean {
  if (!item) return false;
  if (isProductStockUnlimited(item)) return true;

  const type = item?.type;
  if (
    type === "taille_variable" ||
    type === "taille_variable_prix_unique" ||
    type === "taille_unique_avec_couleur"
  ) {
    const variations = item?.variations ?? [];
    if (variations.length === 0) {
      return parseLocaleNumber(item?.stock) > 0;
    }
    return variations.some((v: any) => isVariationInStock(v));
  }
  return parseLocaleNumber(item?.stock) > 0;
}
